/**
 * FreeBoardSection Component
 * 자유게시판 섹션
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, USER_TIERS } from '../../contexts/AuthContext';
import { useCommunity, POST_TYPES, CATEGORIES } from '../../contexts/CommunityContext';
import { formatDate } from './utils';
import CommunityEditor from '../../components/CommunityEditor';

const FreeBoardSection = ({ onPostSelect, selectedCategory: externalCategory, onCategoryChange }) => {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const { getCommunityPosts, getCategoryCount, createPost, refreshPosts } = useCommunity();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL에서 카테고리 초기값 읽기
  const validCategories = Object.values(CATEGORIES);
  const urlCategory = searchParams.get('category');
  const initialUrlCategory = validCategories.includes(urlCategory) ? urlCategory : null;

  // 외부 prop이 있으면 사용, 없으면 URL -> 내부 state 순서
  const [internalCategory, setInternalCategory] = useState(
    externalCategory !== undefined ? externalCategory : initialUrlCategory
  );
  const selectedCategory = externalCategory !== undefined ? externalCategory : internalCategory;
  const setSelectedCategory = useCallback((cat) => {
    if (onCategoryChange) onCategoryChange(cat);
    setInternalCategory(cat);
    // URL 쿼리 파라미터 동기화
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (cat === null || cat === undefined) next.delete('category'); else next.set('category', cat);
      return next;
    }, { replace: true });
    refreshPosts();
  }, [onCategoryChange, refreshPosts, setSearchParams]);

  const [showWriteForm, setShowWriteForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES.GENERAL);
  const [message, setMessage] = useState('');

  // 뒤로가기 시 URL의 category와 내부 state 동기화
  useEffect(() => {
    if (externalCategory !== undefined) return; // 외부 prop 사용 시 무시
    const categoryFromUrl = validCategories.includes(urlCategory) ? urlCategory : null;
    if (categoryFromUrl !== internalCategory) {
      setInternalCategory(categoryFromUrl);
    }
  }, [urlCategory, validCategories, externalCategory, internalCategory]);

  const canPost = isAuthenticated && user?.tier !== USER_TIERS.GUEST;
  const posts = getCommunityPosts(selectedCategory);
  const categoryCounts = getCategoryCount();

  // 카테고리 한글 이름
  const categoryLabels = {
    all: '전체',
    general: '일반',
    question: '질문',
    discussion: '토론',
    showcase: '자랑',
    tip: '팁',
    stock: '주식'
  };

  const handleCreatePost = (e) => {
    e.preventDefault();
    if (!newTitle.trim() || !newContent.trim()) return;

    createPost(POST_TYPES.COMMUNITY, newTitle, newContent, user, newCategory, []);
    setNewTitle('');
    setNewContent('');
    setNewCategory(CATEGORIES.GENERAL);
    setShowWriteForm(false);
    setMessage('게시글이 등록되었습니다.');
    setTimeout(() => setMessage(''), 3000);
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <h1>💬 {t('community.tabs.community', '자유게시판')}</h1>
        <p>자유롭게 이야기를 나눠보세요</p>
      </div>

      {/* 카테고리 탭 */}
      <div className="category-tabs">
        <button
          className={`category-tab ${selectedCategory === null ? 'active' : ''}`}
          onClick={() => setSelectedCategory(null)}
        >
          {categoryLabels.all} <span className="count">{categoryCounts.all || 0}</span>
        </button>
        {Object.values(CATEGORIES).map(cat => (
          <button
            key={cat}
            className={`category-tab ${selectedCategory === cat ? 'active' : ''}`}
            onClick={() => setSelectedCategory(cat)}
          >
            {categoryLabels[cat] || cat} <span className="count">{categoryCounts[cat] || 0}</span>
          </button>
        ))}
      </div>

      {message && <div className="message success">{message}</div>}

      {canPost && !showWriteForm && (
        <button className="write-btn" onClick={() => setShowWriteForm(true)}>
          글쓰기
        </button>
      )}

      {showWriteForm && (
        <form onSubmit={handleCreatePost} className="write-form">
          <select
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            className="category-select"
          >
            {Object.values(CATEGORIES).map(cat => (
              <option key={cat} value={cat}>
                {t(`community.categories.${cat}`, cat)}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="제목을 입력하세요"
            required
          />
          <CommunityEditor
            value={newContent}
            onChange={setNewContent}
            placeholder="내용을 입력하세요"
            minHeight={200}
          />
          <div className="form-actions">
            <button type="button" onClick={() => setShowWriteForm(false)} className="cancel-btn">
              취소
            </button>
            <button type="submit" className="submit-btn">
              등록
            </button>
          </div>
        </form>
      )}

      <div className="posts-list">
        {posts.length === 0 ? (
          <p className="no-posts">등록된 게시글이 없습니다.</p>
        ) : (
          posts.map((post) => (
            <div key={post.id} className="post-card" onClick={() => onPostSelect(post)}>
              <div className="post-card-header">
                {post.category && (
                  <span className={`post-badge category-${post.category}`}>
                    {t(`community.categories.${post.category}`, post.category)}
                  </span>
                )}
                <h3>{post.title}</h3>
              </div>
              <div className="post-card-meta">
                <span className="author">{post.author.name}</span>
                <span className="date">{formatDate(post.createdAt)}</span>
                <span className="comments">댓글 {post.comments?.length || 0}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FreeBoardSection;
